import { lazy } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import { RouteChrome } from '@/components/pwa/RouteChrome';
import RootLayout from './RootLayout';
import CustomerSurfaceLayout from './CustomerSurfaceLayout';
import ErrorBoundary from './ErrorBoundary';
import ProtectedRoute from './ProtectedRoute';
import RequireAuth from './RequireAuth';
import RequireRole from './RequireRole';
import RequireBootstrapAdmin from './RequireBootstrapAdmin';
import RequireEventIntelligence from './RequireEventIntelligence';
import RequirePreparedOfflineEvents from './RequirePreparedOfflineEvents';
import ApprovalAccessDenied from './ApprovalAccessDenied';
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import PendingActivation from '@/pages/PendingActivation';

const HowTo = lazy(() => import('@/pages/HowTo'));
const WhatsNew = lazy(() => import('@/pages/WhatsNew'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const EventOfflineWorkspace = lazy(() => import('@/pages/EventOfflineWorkspace'));
const Events = lazy(() => import('@/pages/Events'));
const EventWorkspace = lazy(() => import('@/pages/EventWorkspace'));
const Analytics = lazy(() => import('@/pages/Analytics'));
const TeamMemberDashboard = lazy(() => import('@/pages/TeamMemberDashboard'));
const Accounts = lazy(() => import('@/pages/Accounts'));
const AccountDetail = lazy(() => import('@/pages/AccountDetail'));
const Quotes = lazy(() => import('@/pages/Quotes'));
const Approvals = lazy(() => import('@/pages/Approvals'));
const Settings = lazy(() => import('@/pages/Settings'));
const Admin = lazy(() => import('@/pages/Admin'));
const AdminUsers = lazy(() => import('@/pages/AdminUsers'));
const QuoteDetail = lazy(() => import('@/pages/QuoteDetail'));
const QuoteNew = lazy(() => import('@/pages/QuoteNew'));
const QuoteEdit = lazy(() => import('@/pages/QuoteEdit'));
const AdminRules = lazy(() => import('@/pages/AdminRules'));
const AdminAuditLog = lazy(() => import('@/pages/AdminAuditLog'));
const AdminCatalog = lazy(() => import('@/pages/AdminCatalog'));
const AdminIntegrations = lazy(() => import('@/pages/AdminIntegrations'));
const AdminWebhooks = lazy(() => import('@/pages/AdminWebhooks'));
const AdminHubSpot = lazy(() => import('@/pages/AdminHubSpot'));
const AdminEmailTemplates = lazy(() => import('@/pages/AdminEmailTemplates'));
const AdminEmailTemplateDetail = lazy(() => import('@/pages/AdminEmailTemplateDetail'));
const AdminDiagnostics = lazy(() => import('@/pages/AdminDiagnostics'));
const AdminAmbiguousAccounts = lazy(() => import('@/pages/AdminAmbiguousAccounts'));
const CustomerQuotePage = lazy(() => import('@/pages/CustomerQuote'));
const CustomerQuotePaidPage = lazy(() => import('@/pages/CustomerQuotePaid'));

export const routes: RouteObject[] = [
  {
    element: <RequirePreparedOfflineEvents />,
    errorElement: <ErrorBoundary />,
    children: [
      { path: '/events/offline', element: <EventOfflineWorkspace /> },
      { path: '/events/offline/:id', element: <EventOfflineWorkspace /> },
    ],
  },
  {
    path: '/login',
    element: <Login />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/callback',
    element: <AuthCallback />,
    errorElement: <ErrorBoundary />,
  },
  {
    element: <CustomerSurfaceLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      { path: '/q/:token', element: <CustomerQuotePage /> },
      { path: '/q/:token/paid', element: <CustomerQuotePaidPage /> },
    ],
  },
  {
    path: '/pending-activation',
    element: <RequireAuth><PendingActivation /></RequireAuth>,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '*',
    loader: () => {
      throw new Response('Not Found', { status: 404 });
    },
    errorElement: <ErrorBoundary />,
  },
  {
    element: (
      <ProtectedRoute>
        <RootLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      { path: '/', element: <Dashboard /> },
      { path: '/how-to', element: <HowTo /> },
      { path: '/how-to/:guideId', element: <HowTo /> },
      { path: '/whats-new', element: <WhatsNew /> },
      {
        element: <RequireEventIntelligence />,
        children: [
          { path: '/events', element: <Events /> },
          { path: '/events/:id', element: <EventWorkspace /> },
        ],
      },
      { path: '/analytics', element: <Analytics /> },
      { path: '/accounts', element: <Accounts /> },
      { path: '/accounts/:id', element: <AccountDetail /> },
      { path: '/quotes', element: <Quotes /> },
      { path: '/quotes/new', element: <QuoteNew /> },
      { path: '/quotes/:id', element: <QuoteDetail /> },
      { path: '/quotes/:id/edit', element: <QuoteEdit /> },
      {
        element: <RequireRole roles={['Manager', 'Admin']} />,
        children: [
          { path: '/team/:userId', element: <TeamMemberDashboard /> },
        ],
      },
      {
        element: (
          <RequireRole
            roles={['Manager', 'Admin']}
            fallback={<ApprovalAccessDenied />}
          />
        ),
        children: [
          { path: '/approvals', element: <Approvals /> },
        ],
      },
      { path: '/settings', element: <Settings /> },
      {
        element: <RequireRole roles={['Admin']} />,
        children: [
          { path: '/admin', element: <Admin /> },
          { path: '/admin/integrations', element: <AdminIntegrations /> },
          { path: '/admin/webhooks', element: <AdminWebhooks /> },
        ],
      },
      {
        element: <RequireRole roles={['Admin']} />,
        children: [
          { path: '/admin/users', element: <AdminUsers /> },
          { path: '/admin/rules', element: <AdminRules /> },
          { path: '/admin/audit-log', element: <AdminAuditLog /> },
          { path: '/admin/catalog', element: <AdminCatalog /> },
          { path: '/admin/hubspot', element: <AdminHubSpot /> },
          { path: '/admin/accounts/ambiguous', element: <AdminAmbiguousAccounts /> },
          { path: '/admin/email-templates', element: <AdminEmailTemplates /> },
          { path: '/admin/email-templates/:configKey', element: <AdminEmailTemplateDetail /> },
          {
            element: <RequireBootstrapAdmin />,
            children: [
              { path: '/admin/diagnostics', element: <AdminDiagnostics /> },
            ],
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter([
  {
    element: <RouteChrome />,
    children: routes,
  },
]);
